import React, { ReactNode, useContext, useMemo, useState } from 'react';

import { ReactFCC } from '@types';
import { noop } from '@utils/general';

import { useCallLogger, LoggerType } from './logger';

export interface IErrorContext {
  callLogger: LoggerType;
  errorState: IApplicationErrorState | null;
  setErrorState: (errors: IApplicationErrorState | null) => void;
  fatalError: boolean;
  setFatalError: (value: boolean) => void;
  clearErrorState: () => void;
}

const defaultValue: IErrorContext = {
  callLogger: noop,
  errorState: null,
  setErrorState: noop,
  fatalError: false,
  setFatalError: noop,
  clearErrorState: noop,
};

export interface IApplicationErrorState {
  header?: string;
  messages: string[];
  messageBold?: boolean;
  icon?: ReactNode;
}

export const ErrorContext = React.createContext<IErrorContext>(defaultValue);

export const ErrorContextProvider: ReactFCC = ({ children }) => {
  const [errorState, setErrorState] = useState<IApplicationErrorState | null>(null);
  const [fatalError, setFatalError] = useState<boolean>(false);
  const callLogger = useCallLogger();

  const stateValue: IErrorContext = useMemo(
    () => ({
      callLogger,
      errorState,
      setErrorState,
      fatalError,
      setFatalError,
      clearErrorState: () => {
        setErrorState(null);
      },
    }),
    [errorState, setErrorState, fatalError, setFatalError, callLogger],
  );

  return <ErrorContext.Provider value={stateValue}>{children}</ErrorContext.Provider>;
};

export const useErrorContext = () => useContext(ErrorContext);
